import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { ActionGroup, Alert, Button, Form, FormAlert, FormGroup, PageSection, Switch, TextInput, Title, ToggleGroup, ToggleGroupItem } from '@patternfly/react-core';
import UserModel from '@app/Model/UserModel';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import FieldsInterface from '@app/Interface/FieldsInterface';
import { getTwoFactorSettingFields, getUserTwoFactorDataForPost } from '@app/Page/TwoFactorSettingsPage/TwoFactorSettingFunctions';
import TypeAssertions from '@app/utils/TypeAssertions';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import UserAuthentication from '@app/Security/UserAuthentication';
import { UserContext } from '@app/Context/UserContext';
import PhoneInput from 'react-phone-number-input/input';
import { E164Number } from 'libphonenumber-js/min';
import htmlParser from '@app/utils/htmlParser';
import UserVerifyAuthFactorModal from '@app/Components/User/UserVerifyAuthFactorModal';

interface TwoFactorSettingsPageProps {
    pageData: PageModel,
    currentUser: UserModel
}

const TwoFactorSettingsPage: React.FC<TwoFactorSettingsPageProps> = ({ pageData, currentUser }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const initialFieldValues: FieldsInterface = getTwoFactorSettingFields(currentUser);

    const [newAuthFactorVerified, setNewAuthFactorVerified] = React.useState(false);

    const [isVerifyModalOpen, setIsVerifyModalOpen] = React.useState(false);

    const [formFields, setFormFields] = React.useState(initialFieldValues);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const twoFactorEnabled = true;

    let defaultNoticeType: "sms" | "email" = "email";

    if (currentUser.twoFactorType === "sms") {
        defaultNoticeType = "sms";
    }

    const [noticeType, setNoticeType] = React.useState<"sms" | "email">(defaultNoticeType);

    const defaultFormValidated = "default";

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [requiredValidated, setRequiredValidated] = React.useState(defaultFormValidated);

    const { userDispatch } = React.useContext(UserContext);

    const handleChange = (newValue: string | boolean, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = newValue;

        setFormFields(updatedFields);

        setNewAuthFactorVerified(false);

    }

    const handlePhoneChange = (newValue: E164Number, fieldId: string) => {

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = String(newValue);

        setFormFields(updatedFields);

        setNewAuthFactorVerified(false);

    }

    // correct event typing is `MouseEvent | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>`
    // TODO: see if there's a way to replace `any` with typing above
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleNoticeTypeClick = (isSelected: boolean, event: any) => {

        const fieldId = event.currentTarget.id;

        const updatedFields = { ...formFields };

        if (isSelected && fieldId === formFields["twoFactorType"].fieldId + "-email") {

            formFields["twoFactorType"].value = "email";

            setFormFields(updatedFields);

            setNoticeType("email");

        } else if (isSelected && fieldId === formFields["twoFactorType"].fieldId + "-sms") {

            formFields["twoFactorType"].value = "sms";

            setFormFields(updatedFields);

            setNoticeType("sms");
        }

        setNewAuthFactorVerified(false);

    }

    const updateUser = async () => {
        const updatedUser = getUserTwoFactorDataForPost(currentUser, formFields);

        const responseCode = await UserAuthentication.saveUser(updatedUser);

        let notificationType: ToastNotificationType = ToastNotificationType.Default;

        let toastTitle = "";

        let toastDescription = "";

        switch (responseCode) {

            case 200:
                notificationType = ToastNotificationType.Success;
                toastTitle = "Success";
                toastDescription = "2FA settings were successfully saved.";

                setNewAuthFactorVerified(false);

                userDispatch({
                    type: "UPDATE",
                    payload: updatedUser
                });

                break;

            case 400:
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Invalid input";
                toastDescription = "Please try again or contact support if the issue persists.";
                break;

            case 404:
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Resource not found";
                toastDescription = "Endpoint to save 2FA settings is invalid, try again or contact support.";
                break;

            case 422:
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Bad user data";
                toastDescription = "2FA data error";
                break;

            default:
                notificationType = ToastNotificationType.Warning;
                toastTitle = "Unknown error";
                toastDescription = "Unable to save 2FA data, contact support.";
                break;
        }

        CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);
    };

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        const validform = formValidation();

        setIsFormProcessing(true);

        if (validform && newAuthFactorVerified) {
            updateUser();
        } else if (validform) {
            setIsVerifyModalOpen(true);
        }

        setIsFormProcessing(false);

    }

    /**
     * Validates form fields
     * @returns boolean
     */
    const formValidation = (): boolean => {

        let validForm = true;

        const updatedFields = { ...formFields };

        setRequiredValidated("default");

        // twoFactorEmailAddress
        if (twoFactorEnabled && noticeType === "email" && typeof formFields["twoFactorEmailAddress"].value === "string" && (TypeAssertions.isStringNullOrEmpty(formFields["twoFactorEmailAddress"].value) || TypeAssertions.isEmail(formFields["twoFactorEmailAddress"].value) === false)) {

            setRequiredValidated("error");

            updatedFields["twoFactorEmailAddress"].validated = "error";

            validForm = false;

        } else {

            updatedFields["twoFactorEmailAddress"].validated = "default";
        }

        // twoFactorPhoneNumber
        const twoFactorPhoneNumber = String(formFields["twoFactorPhoneNumber"].value);

        if (twoFactorEnabled && noticeType === "sms" && typeof formFields["twoFactorPhoneNumber"].value === "string" && (TypeAssertions.isStringNullOrEmpty(twoFactorPhoneNumber) || TypeAssertions.isValidE164Number(twoFactorPhoneNumber) === false)) {

            setRequiredValidated("error");

            updatedFields["twoFactorPhoneNumber"].validated = "error";

            validForm = false;

        } else {

            updatedFields["twoFactorPhoneNumber"].validated = "default";
        }

        setFormFields(updatedFields);

        return validForm;

    }

    /**
     * Resets to previous values by loading current user information
     * @param e 
     */
    const handleReset = (e: React.FormEvent<HTMLButtonElement>) => {

        e.preventDefault();

        setFormFields(initialFieldValues);

        setNoticeType(defaultNoticeType);

    }

    React.useEffect(() => {

        return () => {
            
            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        const maybeSubmitForm = () => {
            if (!mounted || !newAuthFactorVerified) {
                return;
            }

            const validform = formValidation();
            if (validform) {
                updateUser();
            }
        }

        maybeSubmitForm();

        return () => {
            mounted = false;
            controller?.abort();
        };
    }, [newAuthFactorVerified]);

    return (
        <PageSection>
            <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
            <div className="pf-c-content">
                {htmlParser.htmlToJSX(pageData.content)}
            </div>
            <UserVerifyAuthFactorModal 
                isModalOpen={isVerifyModalOpen}
                typeToVerify={noticeType}
                deliveryMethodToVerify={noticeType === 'email'
                    ? `${formFields["twoFactorEmailAddress"].value}`
                    : `${formFields["twoFactorPhoneNumber"].value}`
                }
                handleFactorValidated={() => {
                    setIsVerifyModalOpen(false);
                    setNewAuthFactorVerified(true);
                }}
                handleCloseClicked={() => {
                    setIsVerifyModalOpen(false);
                }}
            />
            <Form autoComplete="false" isWidthLimited>
                {requiredValidated === "error" && (
                    <FormAlert>
                        <Alert
                            variant="danger"
                            title="Fill out all required fields before continuing"
                            aria-live="polite"
                            role="alert"
                            isInline
                        ></Alert>
                    </FormAlert>
                )}
                {twoFactorEnabled &&
                    <FormGroup
                        label={formFields["twoFactorType"].label}
                        isRequired={formFields["twoFactorType"].required}
                        fieldId={formFields["twoFactorType"].formFieldId}
                        helperTextInvalid={formFields["twoFactorType"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["twoFactorType"].validated}
                    >
                        <ToggleGroup aria-label="Verification Type">
                            <ToggleGroupItem
                                text="Email"
                                buttonId={formFields["twoFactorType"].fieldId + "-email"}
                                isSelected={noticeType === "email"}
                                onChange={handleNoticeTypeClick}
                            />
                            <ToggleGroupItem
                                text="SMS"
                                buttonId={formFields["twoFactorType"].fieldId + "-sms"}
                                isSelected={noticeType === "sms"}
                                onChange={handleNoticeTypeClick}
                            />
                        </ToggleGroup>
                    </FormGroup>
                }
                {twoFactorEnabled && noticeType === "email" &&
                    <FormGroup
                        label={formFields["twoFactorEmailAddress"].label}
                        isRequired={formFields["twoFactorEmailAddress"].required}
                        fieldId={formFields["twoFactorEmailAddress"].fieldId}
                        helperTextInvalid={formFields["twoFactorEmailAddress"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["twoFactorEmailAddress"].validated}
                    >
                        <TextInput
                            isRequired={formFields["twoFactorEmailAddress"].required}
                            type="email"
                            id={formFields["twoFactorEmailAddress"].fieldId}
                            name={formFields["twoFactorEmailAddress"].fieldId}
                            value={typeof formFields["twoFactorEmailAddress"].value === "string" ? formFields["twoFactorEmailAddress"].value : ""}
                            onChange={handleChange}
                        />
                    </FormGroup>
                }
                {twoFactorEnabled && noticeType === "sms" &&
                    <FormGroup
                        label={formFields["twoFactorPhoneNumber"].label}
                        isRequired={formFields["twoFactorPhoneNumber"].required}
                        fieldId={formFields["twoFactorPhoneNumber"].fieldId}
                        helperTextInvalid={formFields["twoFactorPhoneNumber"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["twoFactorPhoneNumber"].validated}
                    >
                        <PhoneInput
                            country='US'
                            id={formFields["twoFactorPhoneNumber"].fieldId}
                            name={formFields["twoFactorPhoneNumber"].fieldId}
                            value={typeof formFields["twoFactorPhoneNumber"].value === "string" ? formFields["twoFactorPhoneNumber"].value : ""}
                            onChange={(value: E164Number) => handlePhoneChange(value, formFields["twoFactorPhoneNumber"].fieldId)}
                        />
                    </FormGroup>
                }
                <ActionGroup>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        spinnerAriaValueText={isFormProcessing ? "Saving" : undefined}
                        isLoading={isFormProcessing}
                    >{isFormProcessing ? "Saving" : "Submit"}</Button>
                    <Button variant="link" onClick={handleReset}>Reset</Button>
                </ActionGroup>
            </Form>
        </PageSection>
    )

};

export default TwoFactorSettingsPage;